import { MessageType, type ClientView, type DoorLine, type HandoffCause } from "odyssey-shared";

/**
 * The server's `handoffInitiated` (ADR-106 decision 19, PROTOCOL.md 6.91.0): the character moves to another map on this
 * socket. `name` is the target map's display name, empty for a map with no name; `cause` is why.
 */
export interface HandoffFrame {
  readonly map: string;
  readonly name: string;
  readonly cause: HandoffCause;
}

/**
 * The gateway's `edgeNear` (PROTOCOL.md 6.96.0, ADR-147 decisions 13 and 26): the neighbour whose edge view the page now
 * receives (`near`) or no longer receives, by map id, and the world version that neighbour runs.
 */
export interface EdgeNearFrame {
  readonly map: string;
  readonly version: string;
  readonly near: boolean;
}

/**
 * A handoff or door frame as the connection acts on it.
 */
export type HandoffText = { readonly kind: "handoff"; readonly handoff: HandoffFrame } | ({ readonly kind: "edgeNear" } & EdgeNearFrame) | { readonly kind: "doorRefused"; readonly reason: DoorLine } | { readonly kind: "doorPrompt"; readonly shown: boolean; readonly name: string };

/**
 * Reads `handoffInitiated`, `edgeNear` (6.96.0), `doorRefused` (PROTOCOL.md 6.92.0), `doorPrompt` (6.93.0) and `doorBusy` (6.102.0, read as the door line `busy`). The text schema has already bounded every field.
 *
 * @param message A parsed server message.
 * @returns The frame, or null for any other message.
 */
export function readHandoffText(message: Readonly<Record<string, unknown>> & { readonly type: number }): HandoffText | null {
  if (message.type === MessageType.HandoffInitiated) {
    return { kind: "handoff", handoff: { map: message["map"] as string, name: message["name"] as string, cause: message["cause"] as HandoffCause } };
  }
  if (message.type === MessageType.EdgeNear) {
    return { kind: "edgeNear", map: message["map"] as string, version: message["version"] as string, near: message["near"] as boolean };
  }
  if (message.type === MessageType.DoorRefused) {
    return { kind: "doorRefused", reason: message["reason"] as DoorLine };
  }
  if (message.type === MessageType.DoorBusy) {
    return { kind: "doorRefused", reason: "busy" };
  }
  if (message.type === MessageType.DoorPrompt) {
    return { kind: "doorPrompt", shown: message["shown"] as boolean, name: message["name"] as string };
  }
  return null;
}

/**
 * Empties a client view in place as a join's is: tick 0 and no entity, with tick 0 the only base a first delta may name, so the
 * map reached after a handoff starts from nothing.
 *
 * @param view The connection's view; the object is kept, since the binary dispatch holds it.
 */
export function emptyClientView(view: ClientView): void {
  view.tick = 0;
  view.entities = new Map();
  view.held.clear();
  view.held.set(0, view.entities);
}
